import React, { useEffect, useState, useCallback } from 'react';
import client from '../api/client';
import toast from 'react-hot-toast';

function StatCard({ title, value, subtitle, accent = 'indigo' }) {
  const colors = {
    indigo: 'from-indigo-500/20 to-indigo-600/5 border-indigo-500/30',
    emerald: 'from-emerald-500/20 to-emerald-600/5 border-emerald-500/30',
    amber: 'from-amber-500/20 to-amber-600/5 border-amber-500/30',
    purple: 'from-purple-500/20 to-purple-600/5 border-purple-500/30',
    rose: 'from-rose-500/20 to-rose-600/5 border-rose-500/30',
  };
  return (
    <div className={`bg-gradient-to-br ${colors[accent]} rounded-xl p-5 border`}>
      <p className="text-slate-400 text-xs font-medium uppercase tracking-wider">{title}</p>
      <p className="text-3xl font-bold text-white mt-2">{value ?? '—'}</p>
      {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
    </div>
  );
}

const TYPE_LABELS = {
  purchase: { label: 'Satın Alma', color: 'bg-emerald-500/20 text-emerald-400' },
  spend: { label: 'Harcama', color: 'bg-red-500/20 text-red-400' },
  referral: { label: 'Referans', color: 'bg-blue-500/20 text-blue-400' },
  bonus: { label: 'Bonus', color: 'bg-amber-500/20 text-amber-400' },
  welcome: { label: 'Hoşgeldin', color: 'bg-purple-500/20 text-purple-400' },
};

export default function Revenue() {
  const [overview, setOverview] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [txTotal, setTxTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [txPage, setTxPage] = useState(0);
  const [txFilter, setTxFilter] = useState('');

  const PAGE_SIZE = 20;

  const fetchOverview = useCallback(async () => {
    try {
      const res = await client.get('/api/v1/admin/revenue/overview');
      setOverview(res.data);
    } catch (e) {
      toast.error('Gelir verileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTransactions = useCallback(async () => {
    try {
      const params = { skip: txPage * PAGE_SIZE, limit: PAGE_SIZE };
      if (txFilter) params.tx_type = txFilter;
      const res = await client.get('/api/v1/admin/revenue/transactions', { params });
      setTransactions(res.data.transactions);
      setTxTotal(res.data.total);
    } catch (e) {
      console.error(e);
    }
  }, [txPage, txFilter]);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);
  useEffect(() => { fetchTransactions(); }, [txPage, txFilter, fetchTransactions]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  const subs = overview?.subscriptions || {};
  const platforms = overview?.platforms || {};
  const purchases = overview?.purchases || {};
  const usage = overview?.credit_usage || [];

  return (
    <div className="space-y-8">
      <h2 className="text-2xl font-bold text-white">💰 Gelir & Kredi Paneli</h2>

      {/* ── Purchase Stats ── */}
      <div>
        <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">Kredi Satışları</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Bugün"
            value={purchases.today?.credits || 0}
            subtitle={`${purchases.today?.count || 0} işlem`}
            accent="emerald"
          />
          <StatCard
            title="Bu Hafta"
            value={purchases.week?.credits || 0}
            subtitle={`${purchases.week?.count || 0} işlem`}
            accent="indigo"
          />
          <StatCard
            title="Bu Ay"
            value={purchases.month?.credits || 0}
            subtitle={`${purchases.month?.count || 0} işlem`}
            accent="purple"
          />
          <StatCard
            title="Toplam"
            value={purchases.total?.credits || 0}
            subtitle={`${purchases.total?.count || 0} işlem`}
            accent="amber"
          />
        </div>
      </div>

      {/* ── Distribution Cards ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Subscription Distribution */}
        <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
          <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">Abonelik Dağılımı</h3>
          <div className="space-y-3">
            {Object.entries(subs).map(([tier, count]) => {
              const total = Object.values(subs).reduce((a, b) => a + b, 0);
              const pct = total ? Math.round((count / total) * 100) : 0;
              const tierColors = { free: 'bg-slate-500', premium: 'bg-indigo-500', premium_plus: 'bg-amber-500' };
              return (
                <div key={tier}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-300 capitalize">{tier.replace('_', ' ')}</span>
                    <span className="text-white font-medium">{count} ({pct}%)</span>
                  </div>
                  <div className="w-full bg-slate-700 rounded-full h-2">
                    <div className={`${tierColors[tier] || 'bg-indigo-500'} h-2 rounded-full transition-all`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Platform Distribution */}
        <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
          <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">Platform (Premium)</h3>
          <div className="space-y-3">
            {Object.entries(platforms).length === 0 ? (
              <p className="text-slate-500 text-sm">Henüz veri yok</p>
            ) : (
              Object.entries(platforms).map(([platform, count]) => {
                const icons = { android: '🤖', ios: '🍎', apple: '🍎', google: '🤖', unknown: '❓' };
                return (
                  <div key={platform} className="flex items-center justify-between">
                    <span className="text-slate-300 capitalize">{icons[platform] || '📱'} {platform}</span>
                    <span className="text-white font-bold text-lg">{count}</span>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Credit Usage by Feature */}
        <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
          <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">Kredi Kullanım Dağılımı</h3>
          <div className="space-y-3">
            {usage.length === 0 ? (
              <p className="text-slate-500 text-sm">Henüz veri yok</p>
            ) : (
              usage.map((item) => (
                <div key={item.feature} className="flex items-center justify-between">
                  <span className="text-slate-300 capitalize">{item.feature.replace('_', ' ')}</span>
                  <div className="text-right">
                    <span className="text-white font-bold">{item.total_credits}</span>
                    <span className="text-slate-500 text-xs ml-1">({item.count}x)</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* ── Transactions Table ── */}
      <div className="bg-slate-800 rounded-xl border border-slate-700">
        <div className="flex items-center justify-between p-5 border-b border-slate-700">
          <h3 className="text-lg font-bold text-white">Son İşlemler</h3>
          <select
            value={txFilter}
            onChange={(e) => { setTxFilter(e.target.value); setTxPage(0); }}
            className="bg-slate-700 text-slate-300 text-sm rounded-lg px-3 py-1.5 border border-slate-600 focus:border-indigo-500 outline-none"
          >
            <option value="">Tümü</option>
            <option value="purchase">Satın Alma</option>
            <option value="spend">Harcama</option>
            <option value="referral">Referans</option>
            <option value="bonus">Bonus</option>
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-slate-400 text-xs uppercase border-b border-slate-700">
                <th className="text-left px-5 py-3">Kullanıcı</th>
                <th className="text-left px-5 py-3">Tip</th>
                <th className="text-left px-5 py-3">Özellik</th>
                <th className="text-right px-5 py-3">Miktar</th>
                <th className="text-right px-5 py-3">Bakiye</th>
                <th className="text-right px-5 py-3">Tarih</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx) => {
                const typeInfo = TYPE_LABELS[tx.type] || { label: tx.type, color: 'bg-slate-500/20 text-slate-400' };
                return (
                  <tr key={tx.id} className="border-b border-slate-700/50 hover:bg-slate-700/30 transition">
                    <td className="px-5 py-3">
                      <div className="text-white font-medium text-xs">{tx.user_name}</div>
                      <div className="text-slate-500 text-xs">{tx.user_email}</div>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${typeInfo.color}`}>
                        {typeInfo.label}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-slate-400 text-xs capitalize">{tx.feature || '—'}</td>
                    <td className={`px-5 py-3 text-right font-bold ${tx.amount > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {tx.amount > 0 ? '+' : ''}{tx.amount}
                    </td>
                    <td className="px-5 py-3 text-right text-slate-400">{tx.balance_after}</td>
                    <td className="px-5 py-3 text-right text-slate-500 text-xs">
                      {tx.created_at ? new Date(tx.created_at).toLocaleString('tr-TR') : '—'}
                    </td>
                  </tr>
                );
              })}
              {transactions.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center py-8 text-slate-500">Henüz işlem yok</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {txTotal > PAGE_SIZE && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-slate-700">
            <p className="text-xs text-slate-500">
              {txPage * PAGE_SIZE + 1}–{Math.min((txPage + 1) * PAGE_SIZE, txTotal)} / {txTotal}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setTxPage((p) => Math.max(0, p - 1))}
                disabled={txPage === 0}
                className="px-3 py-1 text-xs bg-slate-700 text-slate-300 rounded-lg disabled:opacity-40 hover:bg-slate-600 transition"
              >
                ← Önceki
              </button>
              <button
                onClick={() => setTxPage((p) => p + 1)}
                disabled={(txPage + 1) * PAGE_SIZE >= txTotal}
                className="px-3 py-1 text-xs bg-slate-700 text-slate-300 rounded-lg disabled:opacity-40 hover:bg-slate-600 transition"
              >
                Sonraki →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
